<template>
  <div :class="['form-group', shouldShowFixedInput || isViewMode ? 'viewmode' : '']">
    <label :for="name">
      {{ props.label ? t(`labels.${props.label}`) : t(`labels.${props.name}`) }}
    </label>

    <InputText
      v-if="shouldShowFixedInput && selectedOption?.name"
      :value="showName(selectedOption.name)"
      readonly
      :disabled="true"
      class="p-inputtext p-component p-filled my-2 w-full my-2"
    />

    <!-- InputGroup with PlaceholderSourceButton -->
    <div
      v-if="!shouldShowFixedInput && (acceptsPlaceholderData || !!props.placeholder)"
      class="p-inputgroup"
      :class="[
        {
          'dirty-state-wrapper': isFieldDirty && !disabled,
          'error-state-wrapper': showError && !disabled,
        },
      ]"
    >
      <PlaceholderSourceButton
        :placeholderSource="props.placeholderSource"
        :placeholder="formattedPlaceholder"
        :currentValue="internalValue?.id"
        :isVisible="isPlaceholderDifferent"
        :disabled="disabled"
        @accept="handleAcceptPlaceholder"
      />
      <Select
        :id="name"
        :key="locale"
        v-model="internalValue"
        class="w-full"
        :options="processedOptions"
        :filter="props.filter ?? false"
        :showClear="props.showClear ?? false"
        optionLabel="searchLabel"
        :placeholder="displayedPlaceholder"
        :class="[
          'w-full',
          {
            'max-w-[260px] truncate': props.compact,
            'dirty-state': (isFieldDirty || differsFromGlobal) && !disabled,
            'error-state': showError,
          },
        ]"
        :disabled="disabled"
        @change="handleChange"
      >
        <template #value="slotProps">
          <!-- Shows selected value -->
          <div v-if="slotProps.value" class="flex align-items-center">
            <div
              :class="props.compact ? 'truncate' : ''"
              :title="props.compact ? showName(slotProps.value.name) : ''"
              :style="props.compact ? 'max-width: 220px;' : ''"
            >
              {{ showName(slotProps.value.name) }}
            </div>
          </div>
          <span v-else>
            {{ slotProps.placeholder }}
          </span>
        </template>
        <template #option="slotProps">
          <div
            :class="props.compact ? 'truncate' : ''"
            :title="props.compact ? showName(slotProps.option.name) : ''"
            :style="props.compact ? 'max-width: 240px;' : ''"
          >
            {{ showName(slotProps.option.name) }}
          </div>
        </template>
      </Select>
    </div>

    <!-- Plain Select (if no InputGroup needed) -->
    <Select
      v-else-if="!shouldShowFixedInput"
      :id="name"
      :key="locale"
      v-model="internalValue"
      class="w-full"
      :options="processedOptions"
      :filter="props.filter ?? false"
      :showClear="props.showClear ?? false"
      optionLabel="searchLabel"
      :placeholder="displayedPlaceholder"
      :class="[
        'w-full',
        {
          'max-w-[260px] truncate': props.compact,
          'dirty-state': (isFieldDirty || differsFromGlobal) && !disabled,
          'error-state': showError,
        },
      ]"
      :disabled="disabled"
      @change="handleChange"
    >
      <template #value="slotProps">
        <!-- Shows selected value -->
        <div v-if="slotProps.value" class="flex align-items-center">
          <div
            :class="props.compact ? 'truncate' : ''"
            :title="props.compact ? showName(slotProps.value.name) : ''"
            :style="props.compact ? 'max-width: 220px;' : ''"
          >
            {{ showName(slotProps.value.name) }}
          </div>
        </div>
        <span v-else>
          {{ slotProps.placeholder }}
        </span>
      </template>
      <template #option="slotProps">
        <div
          :class="props.compact ? 'truncate' : ''"
          :title="props.compact ? showName(slotProps.option.name) : ''"
          :style="props.compact ? 'max-width: 240px;' : ''"
        >
          {{ showName(slotProps.option.name) }}
        </div>
      </template>
    </Select>
    <FormError v-if="showError" :message="errorMessage" />
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref, watch } from 'vue';
import type { SelectChangeEvent } from 'primevue/select';
import { useField, useForm } from 'vee-validate';
import { useI18n } from 'vue-i18n';

import type { InvoiceItemSuggestionSource } from '@/views/operatingcostinvoices/components/operationcostinvoicelines/operatingCostInvoiceTypes';

import PlaceholderSourceButton from './elements/PlaceholderSourceButton.vue';
import FormError from './errors/FormError.vue';

export interface SelectModel {
  id: string | number;
  name: string;
}

const props = defineProps<{
  name: string;
  placeholder?: string;
  options: (string | SelectModel)[];
  modelValue: string | undefined;
  initialValue: string | undefined;
  isDirty: (dirty: boolean) => void;
  label?: string;
  disabled?: boolean;
  filter?: boolean;
  showClear?: boolean;
  untranslated?: boolean;
  globalDefault?: string;
  singleValueFixed?: boolean;
  autofillSingleValue?: boolean;
  compact?: boolean;
  viewMode?: boolean;
  placeholderSource?: InvoiceItemSuggestionSource;
  acceptPlaceholderData?: number; // Trigger to apply placeholder (when provided, auto-enables placeholder acceptance)
}>();

const i18n = useI18n();
const { t, locale } = i18n;
const { errorMessage, meta, validate, setTouched } = useField<string>(props.name, undefined, {
  // do not validate on mount
  validateOnMount: false,
  // do not auto-validate on every model change (we control it)
  validateOnValueUpdate: false,
});

const { submitCount } = useForm();
const showError = computed(() => {
  if (props.disabled) return false;
  if (!errorMessage.value) return false;

  // Show error only:
  // - after user touched the field, or
  // - after the form has been submitted at least once
  return meta.touched || submitCount.value > 0;
});

const showName = (name: unknown) => {
  const label =
    typeof name === 'string'
      ? name
      : typeof name === 'number' || typeof name === 'boolean' || typeof name === 'bigint'
        ? `${name}`
        : '';
  return !!props.untranslated
    ? label
    : t(`enums.${label.toUpperCase().replace(/\s+/g, '_')}`, label);
};

const shouldShowFixedInput = computed(() => {
  return (
    !!props.singleValueFixed &&
    !props.showClear &&
    processedOptions.value.length === 1 &&
    !!internalValue.value?.id
  );
});

const emit = defineEmits<{
  (e: 'update:modelValue', value: string | number | undefined): void;
  (e: 'change', payload: SelectChangeEvent | string | number | undefined): void;
  (e: 'userChange', payload: SelectChangeEvent): void;
}>();

const internalValue = ref<SelectModel | null>(null);
const initialValue = computed<SelectModel | null>(() => {
  const foundOption = props.options.find(
    (obj): obj is SelectModel =>
      typeof obj === 'object' && 'id' in obj && obj.id === props.initialValue,
  );

  if (props.untranslated) {
    return foundOption || null;
  }

  if (foundOption) {
    return foundOption;
  }

  return null;
});

const isFieldDirty = ref(false); // Track dirty state
const isUserModified = ref(false); // Track if user manually selected value
const isViewMode = computed(() => !!props.viewMode);

const differsFromGlobal = computed(() => {
  if (!props.globalDefault) return false;
  return internalValue.value?.id !== props.globalDefault;
});

// Type Guard to check if an option is of type SelectModel
function isSelectModel(option: string | SelectModel): option is SelectModel {
  return typeof option === 'object' && 'id' in option && 'name' in option;
}

const processedOptions = computed(() => {
  const _ = locale.value; // force dependency on locale
  return props.options.map((option) => {
    const obj = isSelectModel(option) ? option : { id: option, name: option };
    return {
      ...obj,
      // Use translated label for both display & filtering
      searchLabel: showName(obj.name), // e.g. "Mietvertrag"
    };
  });
});

const selectedOption = computed(() => {
  return processedOptions.value.find((opt) => String(opt.id) === String(props.modelValue));
});

/**
 * Compute formatted placeholder (same name from option if it exists)
 */
const formattedPlaceholder = computed(() => {
  if (!props.placeholder) return '';

  // Try to find the placeholder value in the options
  const placeholderOption = processedOptions.value.find(
    (opt) => String(opt.id) === String(props.placeholder),
  );

  // Return the name if found, otherwise return the placeholder as string
  return placeholderOption ? showName(placeholderOption.name) : String(props.placeholder);
});

/**
 * Determines if button should be visible
 * Compares current value with placeholder AND checks if placeholder exists in options
 */
const isPlaceholderDifferent = computed(() => {
  if (!props.placeholderSource || !props.placeholder || props.disabled) {
    return false;
  }

  // Check if placeholder value exists in options
  const placeholderExists = processedOptions.value.some(
    (opt) => String(opt.id) === String(props.placeholder),
  );

  if (!placeholderExists) {
    return false; // Don't show button if placeholder doesn't exist in options
  }

  // Check if current value differs from placeholder (use props.modelValue directly for sync comparison)
  return String(props.modelValue ?? '') !== String(props.placeholder);
});

// Trigger is defined (after consolidation, acceptPlaceholderData is a number trigger, not a boolean)
const acceptsPlaceholderData = computed(() => props.acceptPlaceholderData !== undefined);

/**
 * Compute the placeholder text to display
 * If current value equals placeholder, don't show placeholder text
 */
const displayedPlaceholder = computed(() => {
  // If current value already matches the placeholder, hide the placeholder text
  if (internalValue.value && String(internalValue.value.id) === String(props.placeholder)) {
    return '\u00A0';
  }

  if (props.placeholder) {
    return formattedPlaceholder.value;
  }

  return '\u00A0';
});

function maybeAutofill() {
  if (props.autofillSingleValue && processedOptions.value.length === 1 && !internalValue.value) {
    internalValue.value = processedOptions.value[0] || null;
  }
}

watch([() => props.autofillSingleValue, processedOptions], maybeAutofill, { immediate: true });

watch(
  () => props.options,
  () => {
    maybeAutofill();
  },
  { immediate: false, deep: true },
);

watch(processedOptions, () => {
  maybeAutofill();
});

watch(locale, () => {
  if (internalValue.value && !props?.untranslated) {
    internalValue.value =
      processedOptions.value.find((o) => o.id === internalValue.value?.id) || internalValue.value;
  }
});

watch(internalValue, (newValue) => {
  internalValue.value = processedOptions.value.find((o) => o.id === newValue?.id) || newValue;

  emit('update:modelValue', newValue ? newValue.id : undefined);
  emit('change', newValue ? newValue.id : undefined); // `change` mit dem neuen Wert emittieren
});

// Watch for changes in modelValue and update internalValue
// Also watch processedOptions to ensure internalValue is synced when options change
watch(
  [() => props.modelValue, processedOptions],
  ([newValue]) => {
    if (processedOptions.value.length === 0) return; // Skip if options not ready
    internalValue.value =
      processedOptions.value.find((obj) => String(obj.id) === String(newValue)) || null;
  },
  { immediate: true },
);

watch(
  () => props.options,
  (newOptions) => {
    if (newOptions && Array.isArray(newOptions) && newOptions.length > 0) {
      if (internalValue.value === null) {
        internalValue.value =
          processedOptions.value.find(
            (option) =>
              typeof option === 'object' &&
              'id' in option &&
              String(option.id) === String(props.modelValue),
          ) || null;
      }
    }
  },
  { immediate: true },
);

// Compare the ids of internalValue and initialValue instead of the objects
watch([internalValue, initialValue], () => {
  const internalId = internalValue.value?.id;
  const initialId = props.initialValue;
  const isDirty = internalId != initialId; // != makes undefined & null be the same
  isFieldDirty.value = isDirty;
  props.isDirty(isDirty);
});

// Watch for accept placeholder trigger from parent
// Only applies if: user hasn't modified AND placeholder exists AND placeholder exists in options
watch(
  () => props.acceptPlaceholderData,
  () => {
    // Only accept if trigger is provided (not undefined)
    if (props.acceptPlaceholderData === undefined) {
      return;
    }

    // Ignore if user has already modified this field
    if (isUserModified.value) {
      return;
    }

    // Ignore if no placeholder
    if (!props.placeholder || props.disabled) {
      return;
    }

    // Find the option with the placeholder ID
    const placeholderOption = processedOptions.value.find(
      (opt) => String(opt.id) === String(props.placeholder),
    );

    // Apply placeholder if it exists in options
    if (placeholderOption) {
      internalValue.value = placeholderOption;
      emit('update:modelValue', placeholderOption.id);
    }
  },
  { immediate: false }, // Don't run on initial load, only when trigger changes
);

onMounted(() => {
  internalValue.value =
    processedOptions.value.find((obj) => isSelectModel(obj) && obj.id === props.modelValue) || null;
});

/**
 * Accept placeholder from button click
 */
function handleAcceptPlaceholder() {
  if (props.disabled || !props.placeholderSource || !props.placeholder) return;

  // Find the option with the placeholder ID
  const placeholderOption = processedOptions.value.find(
    (opt) => String(opt.id) === String(props.placeholder),
  );

  if (!placeholderOption) return; // Safety check

  // Set to the placeholder option
  internalValue.value = placeholderOption;
  emit('update:modelValue', placeholderOption.id);
}

function handleChange(this: void, event: SelectChangeEvent) {
  // mark as touched & validate only on real user interaction
  setTouched(true);
  void validate();

  // Mark that user manually selected a value
  isUserModified.value = true;

  emit('change', event);
  emit('userChange', event); // Use this in case you only want to subscribe for user changes
}

/**
 * Reset the user modification flag (called when form is reset)
 */
function resetUserModified() {
  isUserModified.value = false;
}

// Expose for parent components
defineExpose({
  isUserModified,
  resetUserModified,
});
</script>

<style scoped>
:deep(.p-inputgroup .p-select.my-2) {
  margin-top: 0 !important;
  margin-bottom: 0 !important;
}
</style>
